// src/components/job-engine/useDashboardTiles.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getDashboardTiles } from "@/api/jobEngine";
import type { DashboardTiles } from "@/api/jobEngine";

const THROTTLE_MS = 2_000;

export interface UseDashboardTilesResult {
  data: DashboardTiles | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  /** Throttled version of refetch — at most one call per 2 seconds. Safe for realtime event bursts. */
  refetchThrottled: () => void;
}

export function useDashboardTiles(): UseDashboardTilesResult {
  const [data, setData] = useState<DashboardTiles | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const lastCallRef = useRef<number>(0);
  const pendingTimerRef = useRef<number | null>(null);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  const refetchThrottled = useCallback(() => {
    const now = Date.now();
    const sinceLast = now - lastCallRef.current;
    if (sinceLast >= THROTTLE_MS) {
      lastCallRef.current = now;
      refetch();
    } else if (pendingTimerRef.current == null) {
      pendingTimerRef.current = window.setTimeout(() => {
        pendingTimerRef.current = null;
        lastCallRef.current = Date.now();
        refetch();
      }, THROTTLE_MS - sinceLast);
    }
  }, [refetch]);

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getDashboardTiles(ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setData(r); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load dashboard tiles");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => {
      ctrl.abort();
      if (pendingTimerRef.current != null) {
        window.clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, [tick]);

  return { data, isLoading, error, refetch, refetchThrottled };
}
